import { useCallback } from 'react';
import { FieldPath, useFormContext } from 'react-hook-form';
import { PaymentMethod } from '@/features/dashboard/tourConfig/types/PaymentMethod.ts';
import { TourInstallmentInputForm } from '@/features/dashboard/tourConfig/schemas/tourInstallmentsSchema.ts';
import useGetPaymentProviderStatus from '@/features/dashboard/paymentProviders/hooks/useGetPaymentProviderStatus.ts';
import { PaymentProviderType } from '@/features/dashboard/paymentProviders/types/PaymentProviderType.ts';
import { TpayVerificationStatus } from '@/features/dashboard/paymentProviders/types/TpayVerificationStatus.ts';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import { useNavigate } from '@tanstack/react-router';
import { SettingsTab } from '@/features/dashboard/companySettings/routes/settingsRoute.ts';

export default function useOnlinePaymentGuard(
  fieldPath: FieldPath<TourInstallmentInputForm>,
) {
  const { setValue } = useFormContext<TourInstallmentInputForm>();
  const { data: statuses } = useGetPaymentProviderStatus();
  const { confirmWithCallback } = confirmDialogUseHook();
  const navigate = useNavigate();

  const guardOnlinePayment = useCallback(
    (values: string[]) => {
      if (!values.includes(PaymentMethod.TPAY)) return;

      const tpay = statuses?.find(
        (s) => s.provider === PaymentProviderType.TPAY,
      );
      const verificationStatus =
        tpay?.verificationStatus ?? TpayVerificationStatus.UNKNOWN;

      if (verificationStatus === TpayVerificationStatus.ACTIVE) return;

      const filtered = values.filter((v) => v !== PaymentMethod.TPAY);
      setValue(fieldPath, filtered as PaymentMethod[]);

      const goToPaymentConfig = () => {
        void navigate({
          to: '/app/company-settings',
          search: { tab: SettingsTab.PAYMENT_CONFIG },
        });
      };

      if (verificationStatus === TpayVerificationStatus.PENDING) {
        confirmWithCallback({
          title: 'Konto TPay oczekuje na weryfikację',
          description:
            'Twoje konto TPay nie zostało jeszcze zweryfikowane. Poczekaj na zakończenie weryfikacji, zanim dodasz płatności online jako metodę płatności.',
          confirmText: 'Przejdź do konfiguracji',
          onConfirm: goToPaymentConfig,
        });
        return;
      }

      confirmWithCallback({
        title: 'Płatności online nie są skonfigurowane',
        description:
          'Aby umożliwić płatności online, musisz najpierw skonfigurować TPay. Czy chcesz przejść do konfiguracji?',
        confirmText: 'Przejdź do konfiguracji',
        onConfirm: goToPaymentConfig,
      });
    },
    [statuses, setValue, fieldPath, confirmWithCallback, navigate],
  );

  return { guardOnlinePayment };
}
